import { useCallback, useMemo } from 'react'
import copy from 'copy-to-clipboard'
import { useTranslation } from 'react-i18next'

import { TMenuItemProps } from 'App/components'
import {
  accountIdSelector,
  isAdminAccountSelector,
  showModalAction,
  useAppDispatch,
  useAppSelector
} from 'store'
import { TChannelVariant } from 'interfaces'
import { UnityHubMarkAsRead } from 'App/components/UnityHub'
import { ReactComponent as PencilIcon } from 'assets/icons/Pencil.svg'
import { ReactComponent as VolumeUpIcon } from 'assets/icons/VolumeUp.svg'
import { ReactComponent as VolumeOffIcon } from 'assets/icons/VolumeOff.svg'
import { ReactComponent as LogoutIcon } from 'assets/icons/Logout.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/Trash.svg'
import { ReactComponent as LinkChainIcon } from 'assets/icons/LinkChain.svg'
import { ReactComponent as PinOutlinedIcon } from 'assets/icons/PinOutlined.svg'
import { toast } from 'App/components/ToastContainer'
import { toastDefaultOptions } from 'globalConstants'
import { generateJoinToUnityHubChannelLink } from 'utils'

import { EModalComponents } from '../../../containers/ModalRoot'
import { useMuteTextChannel } from '../../../containers/Chat/useMuteTextChannel'

import { useCommunityChannelHighlight } from './useCommunityChannelHighlight'

type TUseKebabMenuProps = {
  communityId: string
}

type TMenuItemsProps = {
  channelId: string
  variant: Extract<TChannelVariant, 'chat' | 'news'>
  channelName: string

  inviteToken?: string
  isMuted?: boolean
  isPublic?: boolean
  channelManagerIds?: string[]
  isFirstInGroup?: boolean
  onEditSuccess?: () => void
}

export type TUseKebabMenu = {
  getMenuItems: (props: TMenuItemsProps) => TMenuItemProps[]
}

export const useKebabMenu = ({ communityId }: TUseKebabMenuProps): TUseKebabMenu => {
  const dispatch = useAppDispatch()

  const { t } = useTranslation()

  const accountId = useAppSelector(accountIdSelector)
  const isBusiness = useAppSelector(isAdminAccountSelector)

  const { handleHighlight } = useCommunityChannelHighlight(communityId)
  const { handleUnmuteTextChannel, getInnerMenuItems } = useMuteTextChannel()

  const menuItems = useMemo(
    () => ({
      markAsRead: ({ channelId }: TMenuItemsProps) => ({
        content: '',
        render: () => (
          <UnityHubMarkAsRead channelId={channelId} entity="communities" entityId={communityId} />
        )
      }),
      mute: ({ channelId, isMuted }: TMenuItemsProps) => ({
        content: isMuted
          ? t('unityHub.channelsList.kebabMenu.unmute')
          : t('unityHub.channelsList.kebabMenu.mute'),
        icon: isMuted ? <VolumeUpIcon /> : <VolumeOffIcon />,
        innerMenuItems: isMuted ? undefined : getInnerMenuItems(channelId),
        onClick: isMuted ? () => handleUnmuteTextChannel(channelId) : undefined
      }),
      edit: ({
        channelId,
        channelName,
        channelManagerIds,
        variant,
        isPublic,
        onEditSuccess
      }: TMenuItemsProps) => ({
        hidden: !(isBusiness || channelManagerIds?.some((manId) => manId === accountId)),
        content: t('unityHub.channelsList.kebabMenu.edit'),
        icon: <PencilIcon />,
        onClick: () =>
          dispatch(
            showModalAction({
              modalTitle: t('modal.createChannel.edit.title', { context: variant }),
              modalType: EModalComponents.COMMUNITY_CREATE_CHANNEL,
              modalProps: {
                variant,
                communityId,
                channelName,
                isPublic,
                channelId,
                onEditSuccess
              }
            })
          )
      }),
      leave: ({ channelId, isPublic }: TMenuItemsProps) => ({
        hidden: isPublic || isBusiness,
        content: t('unityHub.channelsList.kebabMenu.leave'),
        icon: <LogoutIcon />,
        onClick: () =>
          dispatch(
            showModalAction({
              modalTitle: t('modal.leaveChannelConfirm.title'),
              modalType: EModalComponents.COMMUNITY_LEAVE_CHANNEL_CONFIRM,
              modalProps: { communityId, channelId }
            })
          )
      }),
      delete: ({ channelId }: TMenuItemsProps) => ({
        hidden: !isBusiness,
        content: t('unityHub.channelsList.kebabMenu.delete'),
        icon: <TrashIcon />,
        onClick: () => {
          dispatch(
            showModalAction({
              modalTitle: t('modal.deleteChannelConfirm.title'),
              modalType: EModalComponents.COMMUNITY_DELETE_TEXT_CHANNEL_CONFIRM,
              modalProps: {
                channelId,
                communityId
              }
            })
          )
        }
      }),
      highlight: ({ channelId, isFirstInGroup }: TMenuItemsProps) => ({
        hidden: !isBusiness,
        content: t('unityHub.channelsList.kebabMenu.moveToHighlights'),
        icon: <PinOutlinedIcon />,
        disabled: isFirstInGroup,
        onClick: () => handleHighlight(channelId, false)
      }),
      invite: ({ isPublic, variant, channelManagerIds, inviteToken }: TMenuItemsProps) => ({
        hidden:
          isPublic || !(isBusiness || channelManagerIds?.some((manId) => manId === accountId)),
        content: t('unityHub.channelsList.kebabMenu.copyLink'),
        icon: <LinkChainIcon />,
        onClick: () => {
          if (!inviteToken) {
            return
          }

          copy(generateJoinToUnityHubChannelLink('community', variant, communityId, inviteToken))

          toast.success(t('common.toast.linkCopied'), toastDefaultOptions)
        }
      })
    }),
    [
      dispatch,
      getInnerMenuItems,
      handleUnmuteTextChannel,
      isBusiness,
      accountId,
      communityId,
      handleHighlight,
      t
    ]
  )

  const getMenuItems = useCallback(
    (props: TMenuItemsProps) => [
      menuItems.markAsRead(props),
      menuItems.mute(props),
      menuItems.edit(props),
      menuItems.highlight(props),
      menuItems.invite(props),
      menuItems.leave(props),
      menuItems.delete(props)
    ],
    [menuItems]
  )

  return {
    getMenuItems
  }
}
